/**
 * ConfirmDialog — a small v2-styled confirmation modal.
 *
 * Replaces native window.confirm() across the train flow (Discard, start-over,
 * finish-with-unsaved-sets). Portaled to document.body at z-index 1000 so it
 * clears the floating bottom nav, same as the exercise picker and rest overlay.
 */
import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

import { FlowButton } from './FlowButton.js';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  /** Confirm button label. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Cancel button label. Defaults to "Cancel". */
  cancelLabel?: string;
  /** Confirm button variant — use "danger" for destructive actions. */
  confirmVariant?: 'primary' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'primary',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCancelRef = useRef(onCancel);
  const titleId = useId();
  const messageId = useId();

  useEffect(() => {
    onCancelRef.current = onCancel;
  }, [onCancel]);

  useEffect(() => {
    if (!open) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const dialog = dialogRef.current;
    const focusableSelector =
      'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusFirstControl = () =>
      (dialog?.querySelector<HTMLElement>(focusableSelector) ?? dialog)?.focus();
    const frame = window.requestAnimationFrame(focusFirstControl);

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancelRef.current();
        return;
      }
      if (event.key !== 'Tab' || !dialog) return;

      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector));
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'color-mix(in srgb, var(--paper-0) 82%, transparent)',
        backdropFilter: 'blur(8px)',
        display: 'grid',
        placeItems: 'center',
        padding: 20,
      }}
    >
      <button
        type="button"
        aria-label="Close confirmation"
        tabIndex={-1}
        onClick={onCancel}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          padding: 0,
          border: 0,
          background: 'transparent',
          cursor: 'default',
        }}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={messageId}
        tabIndex={-1}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 360,
          background: 'var(--paper-1)',
          border: '1px solid var(--edge-strong)',
          borderRadius: 'var(--r-lg)',
          boxShadow: 'var(--elev-1), 0 18px 44px rgba(0,0,0,0.42)',
          padding: 22,
          animation: 'v2-rise 240ms var(--ease-out) both',
        }}
      >
        <h2
          id={titleId}
          style={{
            margin: 0,
            fontFamily: 'var(--font-display)',
            fontSize: 18,
            fontWeight: 700,
            letterSpacing: '-0.01em',
            color: 'var(--ink-1)',
          }}
        >
          {title}
        </h2>
        <p
          id={messageId}
          style={{
            margin: '10px 0 20px',
            fontSize: 'var(--text-body-sm-size)',
            lineHeight: 'var(--text-body-sm-line)',
            color: 'var(--ink-2)',
          }}
        >
          {message}
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <FlowButton variant="soft" block onClick={onCancel}>
            {cancelLabel}
          </FlowButton>
          <FlowButton variant={confirmVariant} block onClick={onConfirm}>
            {confirmLabel}
          </FlowButton>
        </div>
      </div>
    </div>,
    document.body,
  );
}
